// ============================================================================
// Deterministic scoring engine.
//
// Given an Agent and the ScoringConfig, produce a fully reproducible AgentScore
// where every category and metric exposes its raw input, normalized value,
// weight, confidence and sources. There is NO randomness and NO hidden state:
// the same inputs always yield the same output.
//
// Unknown metrics (value === null) are EXCLUDED from the weighted average and
// reported via `coverage` rather than being treated as zero. This prevents
// missing data from silently dragging a score up or down.
// ============================================================================

import type {
  Agent,
  AgentScore,
  CategoryScore,
  Cell,
  Confidence,
  FeeModel,
  MetricDef,
  MetricResult,
  ScoringConfig,
  TrustpilotRef,
} from "@/types";

// Feature ids that also have a dedicated top-level cell on the Agent. When a
// resolver asks for one of these, we prefer the richer of the two signals.
const FEATURE_CELL_FALLBACK: Record<string, keyof Agent> = {
  item_consolidation: "consolidation",
  repackaging: "repackaging",
  free_qc_photos: "freePhotos",
  insurance_option: "insuranceOffered",
};

interface Resolved {
  raw: unknown;
  conf: Confidence;
  src: string[];
}

function fromCell<T>(cell: Cell<T> | undefined | null): Resolved {
  if (!cell) return { raw: null, conf: "unknown", src: [] };
  return { raw: cell.v, conf: cell.conf, src: cell.src };
}

function resolveFeature(agent: Agent, featureId: string): Resolved {
  const direct = agent.features?.[featureId];
  if (direct && direct.v !== null) return fromCell(direct);
  const fallbackKey = FEATURE_CELL_FALLBACK[featureId];
  if (fallbackKey) {
    const cell = agent[fallbackKey] as unknown as Cell<boolean> | undefined;
    if (cell && cell.v !== null) return fromCell(cell);
  }
  if (direct) return fromCell(direct);
  return { raw: null, conf: "unknown", src: [] };
}

function verificationScore(level: Agent["verification"]): number {
  return level === "verified" ? 2 : level === "community" ? 1 : 0;
}

/** Resolve the raw value + provenance for a metric from an agent. */
export function resolveMetric(agent: Agent, metric: MetricDef): Resolved {
  const r = metric.resolver;
  if (r.startsWith("count:")) {
    const key = r.slice("count:".length);
    if (key === "shippingLineIds") {
      return { raw: agent.shippingLineIds.length, conf: "high", src: [] };
    }
    const cell = agent[key as keyof Agent] as unknown as Cell<unknown[]> | undefined;
    if (cell && Array.isArray(cell.v)) return { raw: cell.v.length, conf: cell.conf, src: cell.src };
    return { raw: null, conf: "unknown", src: [] };
  }
  if (r.startsWith("feature:")) {
    return resolveFeature(agent, r.slice("feature:".length));
  }
  if (r.startsWith("cell:")) {
    const key = r.slice("cell:".length) as keyof Agent;
    return fromCell(agent[key] as unknown as Cell<unknown>);
  }
  if (r === "fee:percent") {
    const cell = agent.serviceFee as Cell<FeeModel>;
    if (!cell || cell.v === null) {
      // A confirmed "no service fee" feature implies 0%.
      const noFee = resolveFeature(agent, "no_service_fee");
      if (noFee.raw === true) return { raw: 0, conf: noFee.conf, src: noFee.src };
      return { raw: null, conf: "unknown", src: [] };
    }
    const model = cell.v;
    if (model.kind === "none") return { raw: 0, conf: cell.conf, src: cell.src };
    if (model.kind === "percent" && typeof model.percent === "number")
      return { raw: model.percent, conf: cell.conf, src: cell.src };
    if (model.kind === "hybrid" && typeof model.percent === "number")
      return { raw: model.percent, conf: cell.conf, src: cell.src };
    return { raw: null, conf: "unknown", src: cell.src };
  }
  if (r === "trustpilot") return fromCell(agent.trustpilot as Cell<TrustpilotRef>);
  if (r === "status:active") {
    const cell = agent.status;
    if (cell.v === null) return { raw: null, conf: "unknown", src: cell.src };
    return { raw: cell.v === "active", conf: cell.conf, src: cell.src };
  }
  if (r === "verification") {
    return { raw: verificationScore(agent.verification), conf: "high", src: [] };
  }
  if (r.startsWith("presence:")) {
    const key = r.slice("presence:".length);
    if (key === "trustpilot") {
      const c = agent.trustpilot;
      return { raw: c.v !== null, conf: c.v !== null ? "high" : "medium", src: c.src };
    }
    if (key === "website") {
      const c = agent.website;
      return { raw: c.v !== null, conf: "high", src: c.src };
    }
  }
  return { raw: null, conf: "unknown", src: [] };
}

/** Map a raw value to 0..1, or null when unknown. Pure + deterministic. */
export function normalize(raw: unknown, spec: MetricDef["normalize"]): number | null {
  if (raw === null || raw === undefined) return null;
  switch (spec.kind) {
    case "bool": {
      if (typeof raw !== "boolean") return null;
      const good = spec.goodWhen ?? true;
      return raw === good ? 1 : 0;
    }
    case "linear": {
      const x = Number(raw);
      if (!Number.isFinite(x)) return null;
      let t = (x - spec.min) / (spec.max - spec.min);
      t = Math.max(0, Math.min(1, t));
      return spec.invert ? 1 - t : t;
    }
    case "count": {
      const x = Number(raw);
      if (!Number.isFinite(x)) return null;
      return Math.max(0, Math.min(1, x / spec.max));
    }
    case "trustpilot": {
      const tp = raw as TrustpilotRef | null;
      if (!tp || typeof tp.score !== "number") return null;
      const base = Math.max(0, Math.min(1, tp.score / 5));
      const reviews = typeof tp.reviews === "number" ? tp.reviews : 0;
      // Confidence damping: more reviews -> trust the score more. Deterministic.
      const damp = 0.6 + 0.4 * Math.min(reviews, 500) / 500;
      return base * damp;
    }
    case "presence": {
      return raw ? 1 : 0;
    }
    default:
      return null;
  }
}

export function scoreCategory(
  agent: Agent,
  category: ScoringConfig["categories"][number]
): CategoryScore {
  const metrics: MetricResult[] = [];
  let weightedSum = 0;
  let knownWeight = 0;
  let totalWeight = 0;

  for (const m of category.metrics) {
    totalWeight += m.weight;
    const { raw, conf, src } = resolveMetric(agent, m);
    const normalized = normalize(raw, m.normalize);
    metrics.push({ id: m.id, label: m.label, raw, normalized, weight: m.weight, conf, src });
    if (normalized !== null) {
      weightedSum += normalized * m.weight;
      knownWeight += m.weight;
    }
  }

  const score = knownWeight > 0 ? (weightedSum / knownWeight) * 100 : null;
  const coverage = totalWeight > 0 ? knownWeight / totalWeight : 0;
  return { id: category.id, label: category.label, score, coverage, metrics };
}

export function scoreAgent(agent: Agent, config: ScoringConfig): AgentScore {
  const categories = config.categories.map((c) => scoreCategory(agent, c));
  const byId = new Map(categories.map((c) => [c.id, c]));

  let weightedSum = 0;
  let knownWeight = 0;
  let totalWeight = 0;
  for (const [catId, w] of Object.entries(config.overallWeights)) {
    totalWeight += w;
    const cat = byId.get(catId);
    if (cat && cat.score !== null) {
      weightedSum += cat.score * w;
      knownWeight += w;
    }
  }

  const overall = knownWeight > 0 ? weightedSum / knownWeight : null;
  const overallCoverage = totalWeight > 0 ? knownWeight / totalWeight : 0;
  return { agentId: agent.id, overall, overallCoverage, categories };
}

/** Round for display without affecting stored precision. */
export function round1(n: number | null): number | null {
  return n === null ? null : Math.round(n * 10) / 10;
}
